import React, { memo, startTransition, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AdvancedMarker, InfoWindow, useMap } from '@vis.gl/react-google-maps';
import { mapsUrls, priceOnly } from './mapUtils';
import { getCachedPlaceDetails, loadPlaceDetailsOnce } from './placeDetailsCache';
import {
  hasMeaningfulViewportChange,
  buildViewportRenderCandidates,
  buildListingSpatialIndex,
  buildListingSpatialIndexIncremental,
  getCachedListingSpatialIndex,
  queryListingSpatialIndex,
  shouldBuildListingSpatialIndex,
  shouldEmitListingRenderProgressUpdate,
  shouldIterateLongitudeEntries,
  resolveInitialListingVisibleCount,
  scheduleIdleWork,
  cancelIdleWork,
  buildListingSignature,
  rememberListingSignature,
  getCachedSignatureIndex,
  setListingSpatialIndexCache,
  VIEWPORT_READY_GATING_MIN_LIST_SIZE,
} from './listingSpatialIndex';

function toLatLng(arr) {
  return { lat: arr[0], lng: arr[1] };
}

const ADDRESS_MARKER_Z_INDEX = 1000; /* Keep user work/home pins on top of all other markers */

/** "Open in Google Maps / Apple Maps" row. Without a query, links drop a pin at the exact coordinates. */
function MapAppLinks({ lat, lng, query = null }) {
  const { google: googleUrl, apple: appleUrl } = mapsUrls(lat, lng, null, query);
  return (
    <div className="map-view__store-popup-actions">
      <a href={googleUrl} target="_blank" rel="noopener noreferrer" className="map-view__store-popup-link">
        Open in Google Maps
      </a>
      <a href={appleUrl} target="_blank" rel="noopener noreferrer" className="map-view__store-popup-link">
        Open in Apple Maps
      </a>
    </div>
  );
}

function AddressMarker({ position, color, label, markerKey, openMarkerKey, onOpenMarker }) {
  const open = openMarkerKey === markerKey;
  return (
    <AdvancedMarker
      position={toLatLng(position)}
      zIndex={ADDRESS_MARKER_Z_INDEX}
      onClick={() => onOpenMarker(markerKey)}
    >
      <span className={`map-view__address-icon-inner map-view__address-icon--${color}`} />
      {open && (
        <InfoWindow
          anchor={undefined}
          position={toLatLng(position)}
          pixelOffset={[0, -30]}
          onClose={() => onOpenMarker(null)}
          onCloseClick={() => onOpenMarker(null)}
          shouldFocus={true}
        >
          <div className="map-view__popup-header">
            <div className="map-view__popup-title map-view__address-popup-title">{label}</div>
            <button
              type="button"
              className="map-view__popup-close"
              onClick={() => onOpenMarker(null)}
              aria-label={`Close ${label} details`}
            >
              ×
            </button>
          </div>
          <MapAppLinks lat={position[0]} lng={position[1]} query={label} />
        </InfoWindow>
      )}
    </AdvancedMarker>
  );
}

/** type: 'grocery' | 'gym' — controls icon (cart vs runner) */
function StoreMarker({ store, type = 'grocery', markerKey, isOpen, onOpenMarker }) {
  const pos = { lat: store.lat, lng: store.lng };
  const isPlaceId = store.id && typeof store.id === 'string' && !store.id.startsWith('fav-');

  // All display data (name, rating, userRatingCount) is already on `store` from the
  // initial search. Google Maps URL is constructed from the place ID by mapsUrls().
  // No Place Details fetch needed — saves 1 API call per marker click.
  const { google: googleUrl, apple: appleUrl } = mapsUrls(
    store.lat,
    store.lng,
    isPlaceId ? store.id : null,
    store.name
  );
  const isGrocery = type === 'grocery';
  const showStar = !!store.isFavorite;

  return (
    <AdvancedMarker position={pos} onClick={() => onOpenMarker(markerKey)}>
      <div
        className={`map-view__store-pin${store.isFavorite ? ' map-view__store-pin--favorite' : ''}${!isGrocery && !showStar ? ' map-view__store-pin--gym' : ''}${isOpen ? ' map-view__store-pin--selected' : ''}`}
      >
        <span
          className={`map-view__store-pin-icon map-view__store-pin-icon--${showStar ? 'favorite' : isGrocery ? 'grocery' : 'gym'}`}
          aria-hidden="true"
          {...(showStar && { 'aria-label': 'Favorite' })}
        >
          {showStar ? '★' : isGrocery ? '🛒' : '🏃'}
        </span>
      </div>
      {isOpen && (
        <InfoWindow
          anchor={undefined}
          position={pos}
          pixelOffset={[0, -30]}
          onClose={() => onOpenMarker(null)}
          onCloseClick={() => onOpenMarker(null)}
        >
          <div className="map-view__store-popup">
            <div className="map-view__popup-header">
              <div className="map-view__popup-title map-view__store-popup-name">{store.name}</div>
              <button
                type="button"
                className="map-view__popup-close"
                onClick={() => onOpenMarker(null)}
                aria-label={`Close ${store.name} details`}
              >
                ×
              </button>
            </div>
            {(store.rating != null || store.userRatingCount != null) && (
              <div className="map-view__store-popup-rating">
                {store.rating != null && (
                  <span className="map-view__store-popup-stars">★ {Number(store.rating).toFixed(1)}</span>
                )}
                {store.userRatingCount != null && store.userRatingCount > 0 && (
                  <span className="map-view__store-popup-reviews">
                    ({Number(store.userRatingCount).toLocaleString()} reviews)
                  </span>
                )}
              </div>
            )}
            {store.distanceMi != null && (
              <div className="map-view__store-popup-distance">{store.distanceMi.toFixed(1)} mi away</div>
            )}
            <div className="map-view__store-popup-actions">
              <a
                href={googleUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="map-view__store-popup-link"
              >
                Open in Google Maps
              </a>
              <a href={appleUrl} target="_blank" rel="noopener noreferrer" className="map-view__store-popup-link">
                Open in Apple Maps
              </a>
            </div>
          </div>
        </InfoWindow>
      )}
    </AdvancedMarker>
  );
}

const MemoStoreMarker = memo(StoreMarker);

function FFListingMarker({ listing, markerKey, isOpen, onOpenMarker }) {
  const pos = { lat: listing.lat, lng: listing.lng };
  const price = priceOnly(listing.amount);

  return (
    <AdvancedMarker position={pos} onClick={() => onOpenMarker(markerKey)}>
      <div className={`map-view__ff-icon${isOpen ? ' map-view__ff-icon--selected' : ''}`}>
        <span className="map-view__ff-icon-inner">{price || '-'}</span>
      </div>
      {isOpen && (
        <InfoWindow
          anchor={undefined}
          position={pos}
          pixelOffset={[0, -30]}
          onClose={() => onOpenMarker(null)}
          onCloseClick={() => onOpenMarker(null)}
          shouldFocus={true}
        >
          <div className="map-view__ff-popup">
            <div className="map-view__popup-header">
              <div className="map-view__popup-title map-view__ff-popup-title">{listing.name || ''}</div>
              <button
                type="button"
                className="map-view__popup-close"
                onClick={() => onOpenMarker(null)}
                aria-label={`Close ${listing.name || 'listing'} details`}
              >
                ×
              </button>
            </div>
            <div className="map-view__ff-popup-meta">
              {listing.bedroomCount != null && <span>{listing.bedroomCount} bed</span>}
              {listing.bathroomCount != null && <span>{listing.bathroomCount} bath</span>}
              {listing.amount && <span className="map-view__ff-popup-amount">{listing.amount}</span>}
            </div>
            <div
              className="map-view__ff-popup-pets"
              aria-label={
                listing.petsAllowed === true
                  ? 'Pets allowed'
                  : listing.petsAllowed === false
                    ? 'Pets not allowed'
                    : 'Pets policy unknown'
              }
            >
              <span className="map-view__ff-popup-pets-icon" aria-hidden="true">
                𓃠
              </span>
              {listing.petsAllowed === true ? (
                <span className="map-view__ff-popup-pets-yes"> ✓</span>
              ) : (
                <span className="map-view__ff-popup-pets-no"> ✗</span>
              )}
            </div>
            {listing.propertyUrl && (
              <a
                href={listing.propertyUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="map-view__ff-popup-link"
              >
                Open listing
              </a>
            )}
            <MapAppLinks lat={listing.lat} lng={listing.lng} />
          </div>
        </InfoWindow>
      )}
    </AdvancedMarker>
  );
}

const MemoFFListingMarker = memo(FFListingMarker);

/** Inactive housing requests sit under active ones so they never hide a live listing. */
const INACTIVE_TENANT_Z_INDEX = -1;

function TenantPropertyMarker({
  property,
  markerKey,
  isOpen,
  onOpenMarker,
  isInactive = false,
  onToggleInactive,
  isFavorite = false,
  onToggleFavorite,
}) {
  const pos = { lat: property.lat, lng: property.lng };
  const cost = priceOnly(property.monthlyCost);
  const iconClass = [
    'map-view__tenant-icon',
    isInactive ? 'map-view__tenant-icon--inactive' : '',
    isOpen ? 'map-view__tenant-icon--selected' : '',
  ].filter(Boolean).join(' ');

  return (
    <AdvancedMarker
      position={pos}
      onClick={() => onOpenMarker(markerKey)}
      zIndex={isInactive && !isOpen ? INACTIVE_TENANT_Z_INDEX : undefined}
    >
      <div
        className={iconClass}
        title={property.name || 'Housing request'}
        aria-label={isInactive ? 'Inactive housing request property' : 'Housing request property'}
      >
        <span className="map-view__tenant-icon-inner">{cost || '-'}</span>
        {isFavorite && (
          <span className="map-view__tenant-heart" aria-label="Favorite">
            ♥
          </span>
        )}
      </div>
      {isOpen && (
        <InfoWindow
          anchor={undefined}
          position={pos}
          pixelOffset={[0, -30]}
          onClose={() => onOpenMarker(null)}
          onCloseClick={() => onOpenMarker(null)}
          shouldFocus={true}
        >
          <div className="map-view__ff-popup">
            <div className="map-view__popup-header">
              <div className="map-view__popup-title map-view__ff-popup-title">{property.name || ''}</div>
              <button
                type="button"
                className="map-view__popup-close"
                onClick={() => onOpenMarker(null)}
                aria-label={`Close ${property.name || 'property'} details`}
              >
                ×
              </button>
            </div>
            <div className="map-view__ff-popup-meta">
              {property.bedrooms ? <span>{property.bedrooms} bed</span> : null}
              {property.bathrooms ? <span>{property.bathrooms} bath</span> : null}
              {property.monthlyCost && <span className="map-view__ff-popup-amount">{property.monthlyCost}</span>}
            </div>
            {property.message && (
              <div className="map-view__tenant-popup-message">{property.message}</div>
            )}
            {property.propertyUrl && (
              <a
                href={property.propertyUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="map-view__ff-popup-link"
              >
                Open listing
              </a>
            )}
            {property.propertyUrl && (onToggleFavorite || onToggleInactive) && (
              <div className="map-view__tenant-actions">
                {onToggleFavorite && (
                  <button
                    type="button"
                    className={`map-view__tenant-inactive-btn map-view__tenant-fav-btn${isFavorite ? ' map-view__tenant-fav-btn--on' : ''}`}
                    onClick={() => onToggleFavorite(property.propertyUrl)}
                    aria-pressed={isFavorite}
                  >
                    {isFavorite ? '♥ Favorited' : '♡ Favorite'}
                  </button>
                )}
                {onToggleInactive && (
                  <button
                    type="button"
                    className={`map-view__tenant-inactive-btn${isInactive ? ' map-view__tenant-inactive-btn--active' : ''}`}
                    onClick={() => onToggleInactive(property.propertyUrl)}
                    aria-pressed={isInactive}
                  >
                    {isInactive ? 'Mark active' : 'Mark inactive'}
                  </button>
                )}
              </div>
            )}
            <MapAppLinks lat={property.lat} lng={property.lng} />
          </div>
        </InfoWindow>
      )}
    </AdvancedMarker>
  );
}

const MemoTenantPropertyMarker = memo(TenantPropertyMarker);

function OsmPopup({
  name,
  subtitle,
  googleUrl,
  appleUrl,
  onClose,
  position,
  popupClass,
  rating,
  userRatingCount,
  placeGoogleUrl,
  spotLabel,
  listedAs,
}) {
  const rootClass = popupClass ? `map-view__store-popup ${popupClass}` : 'map-view__store-popup';
  const googleHref = placeGoogleUrl || googleUrl;
  return (
    <InfoWindow anchor={undefined} position={position} pixelOffset={[0, -30]} onClose={onClose} onCloseClick={onClose} shouldFocus={true}>
      <div className={rootClass}>
        <div className="map-view__popup-header">
          <div className="map-view__popup-title map-view__store-popup-name">{name}</div>
          <button
            type="button"
            className="map-view__popup-close"
            onClick={onClose}
            aria-label={`Close ${name} details`}
          >
            ×
          </button>
        </div>
        {spotLabel && <div className="map-view__store-popup-distance">{spotLabel}</div>}
        {listedAs && <div className="map-view__store-popup-distance">Listed as: {listedAs}</div>}
        {(rating != null || (userRatingCount != null && userRatingCount > 0)) && (
          <div className="map-view__store-popup-rating">
            {rating != null && <span className="map-view__store-popup-stars">★ {Number(rating).toFixed(1)}</span>}
            {userRatingCount != null && userRatingCount > 0 && (
              <span className="map-view__store-popup-reviews">({Number(userRatingCount).toLocaleString()} reviews)</span>
            )}
          </div>
        )}
        {subtitle && <div className="map-view__store-popup-distance">{subtitle}</div>}
        <div className="map-view__store-popup-actions">
          <a href={googleHref} target="_blank" rel="noopener noreferrer" className="map-view__store-popup-link">
            Open in Google Maps
          </a>
          <a href={appleUrl} target="_blank" rel="noopener noreferrer" className="map-view__store-popup-link">
            Open in Apple Maps
          </a>
        </div>
      </div>
    </InfoWindow>
  );
}

/**
 * Search for a place by name (optional suffix to bias results, e.g. " ski resort")
 * and fetch rating, review count, Google Maps URI, and display name.
 */
function usePlaceDetailsBySearch(lat, lon, name, open, searchSuffix = '') {
  const [details, setDetails] = useState(null);

  useEffect(() => {
    if (!open || !name || !window.google?.maps?.importLibrary) {
      setDetails(null);
      return;
    }

    const roundedLat = Number(lat).toFixed(3);
    const roundedLon = Number(lon).toFixed(3);
    const cacheKey = `search:${name.toLowerCase()}|${searchSuffix}|${roundedLat},${roundedLon}`;
    const cached = getCachedPlaceDetails(cacheKey);
    if (cached) {
      setDetails(cached);
      return;
    }

    let cancelled = false;
    const textQuery = searchSuffix ? `${name}${searchSuffix}` : name;

    loadPlaceDetailsOnce(cacheKey, () =>
      window.google.maps.importLibrary('places').then(({ Place }) =>
        Place.searchByText({
          textQuery,
          locationBias: { lat, lng: lon },
          maxResultCount: 1,
          fields: ['displayName', 'rating', 'userRatingCount', 'googleMapsURI'],
        })
          .then(({ places }) => {
            if (!places?.length) return null;
            const place = places[0];
            return {
              rating: place.rating,
              userRatingCount: place.userRatingCount,
              googleMapsURI: place.googleMapsURI,
              displayName: place.displayName?.text ?? null,
            };
          })
          .catch(() => null)
      )
    ).then((nextDetails) => {
      if (cancelled || !nextDetails) return;
      setDetails(nextDetails);
    });

    return () => {
      cancelled = true;
    };
  }, [open, lat, lon, name, searchSuffix]);

  return details;
}

function HotSpringMarker({ spring, markerKey, openMarkerKey, onOpenMarker }) {
  const pos = { lat: spring.lat, lng: spring.lon };
  const open = openMarkerKey === markerKey;
  const name = spring.tags?.name || 'Hot Spring';
  const subtitle = [spring.tags?.description, spring.tags?.access ? `Access: ${spring.tags.access}` : null]
    .filter(Boolean)
    .join(' · ') || null;
  const placeDetails = usePlaceDetailsBySearch(spring.lat, spring.lon, name, open);
  const { google: googleUrl, apple: appleUrl } = mapsUrls(spring.lat, spring.lon, null, name);
  const listedAs = placeDetails?.displayName && placeDetails.displayName !== name ? placeDetails.displayName : null;

  return (
    <AdvancedMarker position={pos} onClick={() => onOpenMarker(markerKey)}>
      <div className="map-view__hs-pin" title={name}>
        <span className="map-view__store-pin-icon" aria-hidden="true">
          ♨️
        </span>
      </div>
      {open && (
        <OsmPopup
          name={name}
          spotLabel={`Spot: ${name}`}
          listedAs={listedAs}
          subtitle={subtitle}
          googleUrl={googleUrl}
          appleUrl={appleUrl}
          onClose={() => onOpenMarker(null)}
          position={pos}
          popupClass="map-view__store-popup--hot-spring"
          rating={placeDetails?.rating}
          userRatingCount={placeDetails?.userRatingCount}
          placeGoogleUrl={placeDetails?.googleMapsURI}
        />
      )}
    </AdvancedMarker>
  );
}

export function HotSpringLayer({ springs, openMarkerKey, onOpenMarker }) {
  return (
    <>
      {springs.map((spring) => (
        <HotSpringMarker
          key={spring.id}
          spring={spring}
          markerKey={`hs-${spring.id}`}
          openMarkerKey={openMarkerKey}
          onOpenMarker={onOpenMarker}
        />
      ))}
    </>
  );
}

function SkiResortMarker({ resort, markerKey, openMarkerKey, onOpenMarker }) {
  const pos = { lat: resort.lat, lng: resort.lon };
  const open = openMarkerKey === markerKey;
  const name = resort.tags?.name || 'Ski Resort';
  const placeDetails = usePlaceDetailsBySearch(resort.lat, resort.lon, name, open, ' ski resort');
  const { google: googleUrl, apple: appleUrl } = mapsUrls(resort.lat, resort.lon, null, `${name} ski resort`);

  return (
    <AdvancedMarker position={pos} onClick={() => onOpenMarker(markerKey)}>
      <div className="map-view__store-pin map-view__store-pin--ski" title={name} aria-label="Ski resort">
        <span className="map-view__store-pin-icon" aria-hidden="true">
          ⛷️
        </span>
      </div>
      {open && (
        <OsmPopup
          name={name}
          googleUrl={googleUrl}
          appleUrl={appleUrl}
          onClose={() => onOpenMarker(null)}
          position={pos}
          popupClass="map-view__store-popup--ski-resort"
          rating={placeDetails?.rating}
          userRatingCount={placeDetails?.userRatingCount}
          placeGoogleUrl={placeDetails?.googleMapsURI}
        />
      )}
    </AdvancedMarker>
  );
}

export function SkiResortLayer({ resorts, openMarkerKey, onOpenMarker }) {
  return (
    <>
      {resorts.map((resort) => (
        <SkiResortMarker
          key={resort.id}
          resort={resort}
          markerKey={`ski-${resort.id}`}
          openMarkerKey={openMarkerKey}
          onOpenMarker={onOpenMarker}
        />
      ))}
    </>
  );
}

export function AmenityMarkersLayer({ amenityMarkerGroups, openMarkerKey, onOpenMarker }) {
  return (
    <>
      {amenityMarkerGroups.map((group) =>
        group.enabled
          ? group.items.map((item) => {
              const markerKey = `${group.markerPrefix}-${item.id}`;
              return (
                <MemoStoreMarker
                  key={markerKey}
                  store={item}
                  type={group.type}
                  markerKey={markerKey}
                  isOpen={openMarkerKey === markerKey}
                  onOpenMarker={onOpenMarker}
                />
              );
            })
          : null
      )}
    </>
  );
}

/** Always mount; when showFFMarkers is false render no markers. Keeps Map child tree stable so
 * config toggles (e.g. hide Furnished Finders) don't unmount this layer and break popups.
 *
 * Rendering hundreds of advanced markers in one pass is expensive. We progressively
 * reveal listing markers in chunks so the map becomes interactive faster.
 */
export function ListingMarkersLayer({
  showFFMarkers,
  ffListings,
  openMarkerKey,
  onOpenMarker,
  onRenderProgress,
}) {
  const map = useMap();
  const list = useMemo(() => (showFFMarkers ? ffListings : []), [showFFMarkers, ffListings]);
  const [visibleCount, setVisibleCount] = useState(0);
  const [viewportBounds, setViewportBounds] = useState(null);
  const [listingSpatialIndex, setListingSpatialIndex] = useState(null);
  const lastViewportRef = useRef(null);
  const currentVisibleRef = useRef(0);
  const lastProgressRef = useRef(null);
  const lastRenderCandidatesRevisionRef = useRef(null);

  const emitRenderProgress = useCallback((progress, force = false) => {
    if (!onRenderProgress) return;
    const now = Date.now();
    const normalized = {
      ...progress,
      updatedAt: Number(progress?.updatedAt) || now,
      startedAt: Number(progress?.startedAt) || now,
    };

    if (!force && !shouldEmitListingRenderProgressUpdate(lastProgressRef.current, normalized, now)) {
      return;
    }

    lastProgressRef.current = normalized;
    onRenderProgress(normalized);
  }, [onRenderProgress]);

  useEffect(() => {
    currentVisibleRef.current = visibleCount;
  }, [visibleCount]);

  useEffect(() => {
    if (!map?.getBounds) {
      setViewportBounds(null);
      lastViewportRef.current = null;
      return;
    }

    const updateBounds = () => {
      const bounds = map.getBounds?.();
      if (!bounds) return;
      const north = bounds.getNorthEast?.()?.lat?.();
      const east = bounds.getNorthEast?.()?.lng?.();
      const south = bounds.getSouthWest?.()?.lat?.();
      const west = bounds.getSouthWest?.()?.lng?.();
      if ([north, east, south, west].some((value) => value == null || Number.isNaN(value))) return;

      const nextBounds = { north, east, south, west };
      if (!hasMeaningfulViewportChange(lastViewportRef.current, nextBounds)) {
        return;
      }
      lastViewportRef.current = nextBounds;
      setViewportBounds(nextBounds);
    };

    const listener = map.addListener?.('idle', updateBounds);
    updateBounds();

    return () => {
      if (listener?.remove) listener.remove();
    };
  }, [map]);

  const waitingForInitialViewport = !!map && !viewportBounds && list.length >= VIEWPORT_READY_GATING_MIN_LIST_SIZE;

  useEffect(() => {
    if (!shouldBuildListingSpatialIndex(list.length)) {
      setListingSpatialIndex(null);
      return;
    }

    const signature = buildListingSignature(list);
    const cachedBySignature = getCachedSignatureIndex(signature);
    if (cachedBySignature) {
      setListingSpatialIndexCache(list, cachedBySignature);
      setListingSpatialIndex(cachedBySignature);
      return;
    }

    let cancelled = false;
    const indexAbortController = typeof AbortController !== 'undefined' ? new AbortController() : null;
    const indexStartedAt = Date.now();

    emitRenderProgress({
      phase: 'indexing',
      indexed: 0,
      rendered: currentVisibleRef.current,
      total: list.length,
      sourceTotal: list.length,
      visibleTotal: list.length,
      done: false,
      startedAt: indexStartedAt,
      updatedAt: indexStartedAt,
    }, true);

    // Start index build quickly once the main render settles so pans don't spend long in O(n) filtering.
    const startIndexingHandle = scheduleIdleWork(() => {
      if (cancelled) return;

      const indexChunkSize = list.length > 5000 ? 900 : list.length > 2000 ? 700 : 500;

      buildListingSpatialIndexIncremental(list, {
        cellSize: 0.2,
        chunkSize: indexChunkSize,
        timeout: 80,
        signal: indexAbortController?.signal,
        onProgress: ({ processed, total, done }) => {
          if (cancelled) return;
          const updatedAt = Date.now();
          emitRenderProgress({
            phase: done ? 'rendering' : 'indexing',
            indexed: processed,
            rendered: currentVisibleRef.current,
            total,
            sourceTotal: total,
            visibleTotal: total,
            done: false,
            startedAt: indexStartedAt,
            updatedAt,
          });
        },
      }).then((nextIndex) => {
        if (cancelled || !nextIndex) return;
        setListingSpatialIndexCache(list, nextIndex);
        rememberListingSignature(signature, nextIndex);
        startTransition(() => {
          setListingSpatialIndex(nextIndex);
        });
      });
    }, 40);

    return () => {
      cancelled = true;
      indexAbortController?.abort();
      cancelIdleWork(startIndexingHandle);
    };
  }, [emitRenderProgress, list]);

  const { renderCandidates, renderCandidatesRevision } = useMemo(() => {
    if (waitingForInitialViewport) return { renderCandidates: [], renderCandidatesRevision: 0 };
    if (!viewportBounds) return { renderCandidates: list, renderCandidatesRevision: `full-${list.length}` };
    if (!listingSpatialIndex) {
      const filtered = buildViewportRenderCandidates(list, viewportBounds);
      return {
        renderCandidates: filtered,
        renderCandidatesRevision: `${filtered.length}:${viewportBounds.north.toFixed(3)}:${viewportBounds.east.toFixed(3)}:${viewportBounds.south.toFixed(3)}:${viewportBounds.west.toFixed(3)}`,
      };
    }
    const indexedCandidates = queryListingSpatialIndex(listingSpatialIndex, viewportBounds);
    return {
      renderCandidates: indexedCandidates,
      renderCandidatesRevision: listingSpatialIndex.lastQueryRevision || 0,
    };
  }, [list, viewportBounds, listingSpatialIndex, waitingForInitialViewport]);

  useEffect(() => {
    if (!showFFMarkers) {
      const now = Date.now();
      setVisibleCount(0);
      emitRenderProgress({
        rendered: 0,
        indexed: 0,
        total: 0,
        sourceTotal: list.length,
        visibleTotal: 0,
        done: true,
        startedAt: now,
        updatedAt: now,
        phase: 'idle',
      }, true);
      return;
    }

    if (waitingForInitialViewport) {
      const now = Date.now();
      setVisibleCount(0);
      emitRenderProgress({
        phase: 'viewport',
        rendered: 0,
        indexed: listingSpatialIndex ? list.length : 0,
        total: list.length,
        sourceTotal: list.length,
        visibleTotal: 0,
        done: false,
        startedAt: now,
        updatedAt: now,
      }, true);
      return;
    }

    const total = renderCandidates.length;
    const startedAt = Date.now();
    if (total === 0) {
      setVisibleCount(0);
      emitRenderProgress({
        rendered: 0,
        indexed: listingSpatialIndex ? list.length : 0,
        total,
        sourceTotal: list.length,
        visibleTotal: total,
        done: true,
        startedAt,
        updatedAt: startedAt,
      }, true);
      return;
    }

    const INITIAL_BATCH = total > 2200 ? 150 : total > 1000 ? 120 : 90;
    const BASE_STEP = total > 3500 ? 320 : total > 2000 ? 260 : total > 1000 ? 220 : 160;

    let cancelled = false;
    let scheduledTask = null;

    const revisionChanged = lastRenderCandidatesRevisionRef.current !== renderCandidatesRevision;
    lastRenderCandidatesRevisionRef.current = renderCandidatesRevision;

    const startCount = resolveInitialListingVisibleCount({
      currentVisibleCount: currentVisibleRef.current,
      total,
      initialBatch: INITIAL_BATCH,
      revisionChanged,
    });
    startTransition(() => {
      setVisibleCount(startCount);
    });
    emitRenderProgress({
      rendered: startCount,
      indexed: listingSpatialIndex ? list.length : 0,
      total,
      sourceTotal: list.length,
      visibleTotal: total,
      done: startCount >= total,
      startedAt,
      updatedAt: Date.now(),
      phase: 'rendering',
    }, true);

    const pump = (current, deadline) => {
      if (cancelled) return;

      let burstMultiplier = 1;
      if (deadline?.didTimeout) {
        burstMultiplier = 1;
      } else if (deadline?.timeRemaining) {
        const idleBudget = deadline.timeRemaining();
        if (idleBudget >= 24) burstMultiplier = 3;
        else if (idleBudget >= 12) burstMultiplier = 2;
      }

      const next = Math.min(current + BASE_STEP * burstMultiplier, total);
      startTransition(() => {
        setVisibleCount(next);
      });
      emitRenderProgress({
        rendered: next,
        indexed: listingSpatialIndex ? list.length : 0,
        total,
        sourceTotal: list.length,
        visibleTotal: total,
        done: next >= total,
        startedAt,
        updatedAt: Date.now(),
        phase: 'rendering',
      });
      if (next < total) {
        scheduledTask = scheduleIdleWork((nextDeadline) => pump(next, nextDeadline));
      }
    };

    if (startCount < total) {
      scheduledTask = scheduleIdleWork((deadline) => pump(startCount, deadline));
    }

    return () => {
      cancelled = true;
      cancelIdleWork(scheduledTask);
    };
  }, [emitRenderProgress, renderCandidatesRevision, renderCandidates, showFFMarkers, waitingForInitialViewport, listingSpatialIndex, list.length]);

  const visibleLimit = Math.min(visibleCount, renderCandidates.length);
  const markerElementCacheRef = useRef({
    renderCandidates: null,
    renderCandidatesRevision: null,
    openMarkerKey: null,
    visibleLimit: 0,
    elements: [],
    markerIndexByKey: new Map(),
  });

  const markers = useMemo(() => {
    const cache = markerElementCacheRef.current;

    const buildMarkerElement = (listing, markerKey) => (
      <MemoFFListingMarker
        key={listing.id}
        listing={listing}
        markerKey={markerKey}
        isOpen={openMarkerKey === markerKey}
        onOpenMarker={onOpenMarker}
      />
    );

    const sameCandidates = cache.renderCandidates === renderCandidates;
    const sameRevision = cache.renderCandidatesRevision === renderCandidatesRevision;
    const canAppendOnly = sameCandidates && sameRevision && visibleLimit >= cache.visibleLimit;

    if (sameCandidates && sameRevision && visibleLimit === cache.visibleLimit && cache.openMarkerKey === openMarkerKey) {
      return cache.elements;
    }

    if (sameCandidates && sameRevision && visibleLimit === cache.visibleLimit && cache.openMarkerKey !== openMarkerKey) {
      const prevOpenKey = cache.openMarkerKey;
      const nextOpenKey = openMarkerKey;
      const prevIndex = prevOpenKey ? cache.markerIndexByKey.get(prevOpenKey) : undefined;
      const nextIndex = nextOpenKey ? cache.markerIndexByKey.get(nextOpenKey) : undefined;

      if (
        (prevIndex == null || prevIndex >= visibleLimit)
        && (nextIndex == null || nextIndex >= visibleLimit)
      ) {
        markerElementCacheRef.current = {
          ...cache,
          renderCandidatesRevision,
          openMarkerKey,
        };
        return cache.elements;
      }

      const nextElements = cache.elements.slice();
      if (prevIndex != null && prevIndex < visibleLimit) {
        const prevListing = renderCandidates[prevIndex];
        const prevKey = `ff-${prevListing.id}`;
        nextElements[prevIndex] = buildMarkerElement(prevListing, prevKey);
      }
      if (nextIndex != null && nextIndex < visibleLimit && nextIndex !== prevIndex) {
        const nextListing = renderCandidates[nextIndex];
        const nextKey = `ff-${nextListing.id}`;
        nextElements[nextIndex] = buildMarkerElement(nextListing, nextKey);
      }

      markerElementCacheRef.current = {
        ...cache,
        renderCandidatesRevision,
        openMarkerKey,
        elements: nextElements,
      };

      return nextElements;
    }

    const nextElements = canAppendOnly ? cache.elements.slice() : [];
    const markerIndexByKey = canAppendOnly ? new Map(cache.markerIndexByKey) : new Map();
    const startIndex = canAppendOnly ? cache.visibleLimit : 0;

    // Reused elements keep their old isOpen value, so rebuild the previously and newly
    // open markers when the open popup changed during a progressive-render step.
    if (canAppendOnly && cache.openMarkerKey !== openMarkerKey) {
      [cache.openMarkerKey, openMarkerKey].forEach((key) => {
        const index = key ? markerIndexByKey.get(key) : undefined;
        if (index != null && index < startIndex) {
          nextElements[index] = buildMarkerElement(renderCandidates[index], key);
        }
      });
    }

    for (let i = startIndex; i < visibleLimit; i += 1) {
      const listing = renderCandidates[i];
      const markerKey = `ff-${listing.id}`;
      nextElements.push(buildMarkerElement(listing, markerKey));
      markerIndexByKey.set(markerKey, i);
    }

    markerElementCacheRef.current = {
      renderCandidates,
      renderCandidatesRevision,
      openMarkerKey,
      visibleLimit,
      elements: nextElements,
      markerIndexByKey,
    };

    return nextElements;
  }, [openMarkerKey, onOpenMarker, renderCandidates, renderCandidatesRevision, visibleLimit]);

  return <>{markers}</>;
}

export function AddressMarkersLayer({
  hasWork,
  hasHome,
  workCenter,
  homeCenter,
  workAddress,
  homeAddress,
  openMarkerKey,
  onOpenMarker,
}) {
  return (
    <>
      {hasWork && (
        <AddressMarker
          position={workCenter}
          color="blue"
          label={workAddress || 'Work'}
          markerKey="address-work"
          openMarkerKey={openMarkerKey}
          onOpenMarker={onOpenMarker}
        />
      )}
      {hasHome && (
        <AddressMarker
          position={homeCenter}
          color="red"
          label={homeAddress || 'Home'}
          markerKey="address-home"
          openMarkerKey={openMarkerKey}
          onOpenMarker={onOpenMarker}
        />
      )}
    </>
  );
}

export function TenantPropertiesLayer({
  properties,
  openMarkerKey,
  onOpenMarker,
  inactiveUrls = [],
  onToggleInactive = null,
  favoriteUrls = [],
  onToggleFavorite = null,
}) {
  const inactiveSet = useMemo(() => new Set(inactiveUrls), [inactiveUrls]);
  const favoriteSet = useMemo(() => new Set(favoriteUrls), [favoriteUrls]);
  return (
    <>
      {properties.map((property) => {
        const markerKey = `tenant-${property.id}`;
        return (
          <MemoTenantPropertyMarker
            key={markerKey}
            property={property}
            markerKey={markerKey}
            isOpen={openMarkerKey === markerKey}
            onOpenMarker={onOpenMarker}
            isInactive={inactiveSet.has(property.propertyUrl)}
            onToggleInactive={onToggleInactive}
            isFavorite={favoriteSet.has(property.propertyUrl)}
            onToggleFavorite={onToggleFavorite}
          />
        );
      })}
    </>
  );
}
